import React from 'react';
import { Package, Wrench, ClipboardCheck, Truck, Cog, Zap, Shield, Globe } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const ServicesSection = () => {
  const mainServices = [
    {
      icon: Package,
      title: 'Aircraft Spare Parts Distribution',
      description: 'Access to a global network of certified suppliers and manufacturers. Our extensive sourcing network spans major aviation markets worldwide, providing access to a comprehensive inventory of aircraft spare parts and components.',
      features: [
        'Global supplier network',
        'OEM certified parts',
        'Common and hard-to-find components',
        'Fast procurement services'
      ]
    },
    {
      icon: Wrench,
      title: 'Aviation Component Supply',
      description: 'We supply a wide range of aviation components across all major aircraft systems including avionics, airframe, engine parts, and electrical components.',
      features: [
        'Avionics systems',
        'Airframe components',
        'Engine parts & accessories',
        'Electrical system parts'
      ]
    },
    {
      icon: ClipboardCheck,
      title: 'Repair and Maintenance Support',
      description: 'Comprehensive coordination with certified repair facilities worldwide. We manage documentation, track repair progress, and ensure regulatory compliance throughout the process.',
      features: [
        'Certified repair coordination',
        'Complete repair management',
        'Documentation handling',
        'Compliance assurance'
      ]
    },
    {
      icon: Truck,
      title: 'Logistics and Global Shipping',
      description: 'Efficient worldwide delivery with real-time tracking. Our logistics team manages customs documentation and clearance procedures to ensure smooth international deliveries.',
      features: [
        'Real-time shipment tracking',
        'Customs clearance support',
        'Expedited services available',
        'Strategic Dubai location'
      ]
    }
  ];

  const componentTypes = [
    { icon: Cog, name: 'Avionics Systems', desc: 'Navigation, communication, and flight control' },
    { icon: Zap, name: 'Airframe Components', desc: 'Structural parts and control surfaces' },
    { icon: Shield, name: 'Engine Parts', desc: 'Turbine components and fuel systems' },
    { icon: Globe, name: 'Electrical Systems', desc: 'Wiring, connectors, and components' }
  ];

  return (
    <section id="services" className="py-16 sm:py-20 md:py-24 bg-[#0B1C2C] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-14 sm:mb-16 space-y-3 sm:space-y-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
            Our <span className="text-[#D4AF37]" style={{textShadow: '0 0 30px rgba(212,175,55,0.3)'}}>Services</span>
          </h2>
          <p className="text-base sm:text-lg text-white/70 max-w-3xl mx-auto px-4">
            Comprehensive aviation supply chain solutions designed to keep your aircraft operational
          </p>
        </div>

        {/* Main Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-14 sm:mb-16">
          {mainServices.map((service, index) => (
            <Card
              key={index}
              data-animate="slide-up"
              data-tilt
              data-testid={`service-card-${index}`}
              style={{ animationDelay: `${index * 0.12}s` }}
              className="group backdrop-blur-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] hover:border-[#D4AF37]/40 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_8px_40px_rgba(212,175,55,0.15)] rounded-2xl"
            >
              <CardContent className="p-6 sm:p-8 space-y-4 sm:space-y-5">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-2.5 sm:p-3 group-hover:bg-[#D4AF37]/20 group-hover:scale-105 transition-all duration-500">
                      <service.icon className="w-full h-full text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#D4AF37] transition-colors duration-300 leading-snug">
                      {service.title}
                    </h3>
                  </div>
                </div>

                <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-2 mt-4">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-white/70">
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3 shadow-[0_0_6px_rgba(212,175,55,0.6)]"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Component Types */}
        <div className="mb-14">
          <h3 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10 tracking-tight">
            Component <span className="text-[#D4AF37]" style={{textShadow: '0 0 20px rgba(212,175,55,0.3)'}}>Categories</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {componentTypes.map((type, index) => (
              <div
                key={index}
                data-testid={`component-type-${index}`}
                className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/[0.07] hover:border-[#D4AF37]/40 hover:-translate-y-1 hover:shadow-[0_8px_40px_rgba(212,175,55,0.12)] transition-all duration-500 group"
              >
                <type.icon className="w-10 h-10 text-[#D4AF37] mb-4 group-hover:scale-105 transition-transform duration-500" strokeWidth={1.5} />
                <h4 className="text-lg font-bold text-white mb-2">{type.name}</h4>
                <p className="text-sm text-white/65">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center">
          <a href="/contact" className="inline-block w-full sm:w-auto px-4">
            <Button
              variant="outline"
              data-testid="services-cta-quote"
              className="w-full sm:w-auto border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B1C2C] px-8 py-6 text-sm uppercase tracking-wider rounded-sm transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(212,175,55,0.45)] cursor-pointer"
            >
              Request a Quote
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
